import React, { useContext, useState } from 'react'
import { Loading3QuartersOutlined } from '@ant-design/icons'

import { useService } from '../../../hooks/use-service'
import { DomainIntegrationService } from '../../../services'
import { simpleNotification } from '../../../_utils/utils'
import { IntegrationProps } from '../interfaces'
import CopyToClipboard from 'react-copy-to-clipboard'
import moment from 'moment/moment'
import { GetWordpressDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PlatformIntegrationCard } from '../../platform-integrations/platform-integration-card/platform-integration-card'
import { PlatformIntegrationModal } from '../../platform-integrations/platform-integration-modal/platform-integration-modal'
import { DomainIntegrationsManagerContext } from '../context'
import { DomainIntegrationsManagerDispatchContext } from '../reducer'
import { Descriptions, Result } from 'antd'
import { AntdClassnameTokens } from '../../../styles/antd-classname-tokens'
import { WordpressApiKeyDialogState } from '../interfaces'

const WordpressIntegration = (props: IntegrationProps<GetWordpressDomainIntegrationResponse>) => {
    const { loading, domain } = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)

    const mode = props.mode
    const integration = mode === 'edit' ? props.integration : null

    const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
    const [apiKeyDialog, setApiKeyDialog] = useState<WordpressApiKeyDialogState>({ status: 'hidden' })
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [removeActionLoading, setRemoveActionLoading] = useState(false)
    const svc = useService<DomainIntegrationService>(DomainIntegrationService)

    const wordpressLogo = <span className="wordpress-logo-color" />

    // holds the integration returned from a successful key creation until the user closes the modal
    const createdIntegrationRef = React.useRef<GetWordpressDomainIntegrationResponse | null>(null)

    const createIntegration = async () => {
        if (loading || mode !== 'add') return
        setPrimaryActionLoading(true)
        setApiKeyDialog({ status: 'creating' })

        const res = await svc.completeIntegrationSetup(
            domain.id,
            DomainIntegrationProviders.WORDPRESS,
            {},
            {
                cancellationKey: 'wordpress-integration',
            },
        )

        setPrimaryActionLoading(false)

        const wpApiToken = res.data?.data?.token
        if (!res.ok || !wpApiToken) {
            setApiKeyDialog({ status: 'failed' })
            return
        }

        setApiKeyDialog({ status: 'created', token: wpApiToken })
        // capture the created integration so it can be added to the list on close
        createdIntegrationRef.current = res.data!
    }

    const closeApiKeyDialog = () => {
        const created = createdIntegrationRef.current
        setApiKeyDialog({ status: 'hidden' })
        if (created) {
            createdIntegrationRef.current = null
            dispatch({ type: 'add-integration', integration: created })
        }
    }

    const removeIntegration = () => {
        if (!integration) return
        setPrimaryActionLoading(true)
        setRemoveDialogOpen(true)
    }

    const confirmRemoveIntegration = async () => {
        if (!integration) return
        setRemoveActionLoading(true)

        const { ok } = await svc.removeIntegration(integration.domain_id, integration.id)

        setRemoveActionLoading(false)

        if (ok) {
            setRemoveDialogOpen(false)
            setPrimaryActionLoading(false)
            simpleNotification('success', 'Wordpress API Key was successfully invalidated.')
            dispatch({ type: 'rem-integration', integration })
        }
    }

    const cancelRemoveIntegration = () => {
        setRemoveDialogOpen(false)
        setPrimaryActionLoading(false)
    }

    return (
        <>
            <PlatformIntegrationCard
                loading={loading}
                provider={DomainIntegrationProviders.WORDPRESS}
                logo={wordpressLogo}
                mode={integration?.id ? 'edit' : 'add'}
                isActive={integration?.is_active}
                actions={{
                    primary: {
                        loading: primaryActionLoading,
                        title: integration?.id ? 'Invalidate API Key' : 'New API Key',
                        onClick: () => {
                            if (!integration) {
                                createIntegration()
                            } else {
                                removeIntegration()
                            }
                        },
                    },
                }}
            >
                {integration && (
                    <Descriptions
                        classNames={AntdClassnameTokens.Descriptions()}
                        layout="horizontal"
                        column={1}
                        items={[
                            {
                                label: 'Status',
                                children: 'Enabled',
                                classNames: { label: 'text-size--small' },
                            },
                            {
                                label: 'Last Active',
                                children: integration.data.token_last_used_at
                                    ? moment(integration.data.token_last_used_at).format('LL')
                                    : 'Never',
                            },
                        ]}
                    />
                )}
            </PlatformIntegrationCard>

            {/* API key creation modal — creating / created / failed states */}
            <PlatformIntegrationModal
                className="wordpress-api-key-dialog"
                open={apiKeyDialog.status !== 'hidden'}
                providerLogo={wordpressLogo}
                title={
                    apiKeyDialog.status === 'creating'
                        ? 'Creating WordPress API Key'
                        : apiKeyDialog.status === 'created'
                        ? 'WordPress API Key Created'
                        : 'WordPress API Key Creation Failed'
                }
                okText={apiKeyDialog.status === 'creating' ? undefined : 'Close'}
                okButtonProps={{ disabled: apiKeyDialog.status === 'creating' }}
                cancelButtonProps={{ style: { display: 'none' } }}
                onOk={closeApiKeyDialog}
                onCancel={closeApiKeyDialog}
            >
                {apiKeyDialog.status === 'creating' && (
                    <Result icon={<Loading3QuartersOutlined spin={true} />} title={false} />
                )}
                {apiKeyDialog.status === 'created' && (
                    <div className="api-key-success-modal-content">
                        <div>
                            <span>
                                The following API Key should be saved on the WordPress plugin Settings page in the{' '}
                                <b>API Key</b> text box. Click the key to copy it your clipboard.
                            </span>
                        </div>
                        <div className="api-key-viewable">
                            <CopyToClipboard
                                text={apiKeyDialog.token}
                                onCopy={() =>
                                    simpleNotification('success', 'WordPress API Key has been copied to your clipboard')
                                }
                            >
                                <span>{apiKeyDialog.token}</span>
                            </CopyToClipboard>
                        </div>
                        <div>
                            <span>
                                Once this window is closed you will not be able to access this key again. In the event
                                of the key being lost, a new key must be created at which point this key will no longer
                                be valid.
                            </span>
                        </div>
                    </div>
                )}
                {apiKeyDialog.status === 'failed' && (
                    <Result
                        status="warning"
                        title={false}
                        subTitle="An error occurred while attempting to create the Wordpress API Key."
                    />
                )}
            </PlatformIntegrationModal>

            {/* Invalidate API key confirmation modal */}
            <PlatformIntegrationModal
                className="wordpress-remove-integration-dialog"
                open={removeDialogOpen}
                providerLogo={wordpressLogo}
                title="Invalidate WordPress API Key"
                okText="Complete"
                okButtonProps={{ type: 'remove', loading: removeActionLoading }}
                onOk={confirmRemoveIntegration}
                onCancel={cancelRemoveIntegration}
            >
                <p>
                    Performing this step will invalidate the WordPress API Key attached to this domain resulting in
                    notifications no longer being sent when articles are published.
                </p>
                <p>
                    In order to disable prompting visitors to subscribe to push when they visit your site you will also
                    need to navigate to your WordPress Admin and uninstall the plugin.
                </p>
            </PlatformIntegrationModal>
        </>
    )
}

export default WordpressIntegration
